import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import type { HubConnection } from '@microsoft/signalr';
import {
  useUnseenNotificationCount,
  useGetUnseenNotification,
  usePostSeenNotification,
  useDeleteNotification,
  useDeleteAllNotifications,
  createSignalRConnection,
} from '@packages/shared/notification';
import type { NotificationModalItem } from '@packages/shared/notification';
import { axiosInstance } from '@/services/apiInterceptor';
import AuthService from '@/services/authService';
import { NotificationRouteList } from '@/constants/notificationRouteList';

export interface UseNotificationsResult {
  count: number | undefined;
  refetchCount: () => void;
  notifications: NotificationModalItem[];
  notificationsLoading: boolean;
  deletingId: string;
  onDelete: (notificationId: string) => void;
  onClearAll: () => void;
  onItemClick: (item: NotificationModalItem) => void;
  formatDate: (dateStr: string) => string;
}

interface UseNotificationsOptions {
  token: string | null;
  onCloseModal?: () => void;
}

/**
 * Customer-specific notifications hook.
 * Composes shared notification hooks with customer-specific SignalR connection,
 * route-based deep linking, and moment.js date formatting.
 *
 * Must be rendered inside `<AuthGate>` — no auth guard needed.
 */
export function useNotifications({ token, onCloseModal }: UseNotificationsOptions): UseNotificationsResult {
  const navigate = useNavigate();
  const isEnabled = Boolean(token);

  const { data: count, refetch: refetchCount } = useUnseenNotificationCount(isEnabled);
  const { data: unseenData, isLoading: notificationsLoading } = useGetUnseenNotification(isEnabled);
  const { mutate: markSeen } = usePostSeenNotification();
  const { mutate: deleteSingle } = useDeleteNotification();
  const { mutate: deleteAll } = useDeleteAllNotifications();

  const [liveMessages, setLiveMessages] = useState<NotificationModalItem[]>([]);
  const [deletingId, setDeletingId] = useState('');

  const notifications: NotificationModalItem[] = [
    ...liveMessages,
    ...(unseenData ?? []).filter(
      (item) => !liveMessages.some((m) => m.notificationId === item.notificationId)
    ),
  ];

  const onDelete = useCallback((notificationId: string) => {
    setDeletingId(notificationId);
    deleteSingle(notificationId, {
      onSuccess: () => {
        setDeletingId('');
        setLiveMessages((prev) => prev.filter((m) => m.notificationId !== notificationId));
      },
      onError: () => setDeletingId(''),
    });
  }, [deleteSingle]);

  const onClearAll = useCallback(() => {
    deleteAll(undefined, {
      onSuccess: () => setLiveMessages([]),
    });
  }, [deleteAll]);

  const onItemClick = useCallback((item: NotificationModalItem) => {
    if (item.notificationId) markSeen(item.notificationId);
    const route = NotificationRouteList.find(
      (r) => r.title === item.notificationRedirectionPage
    );
    if (route) navigate(route.url);
    onCloseModal?.();
  }, [markSeen, navigate, onCloseModal]);

  const formatDate = useCallback(
    (dateStr: string) => moment(dateStr).format('DD MMM. YYYY h:mm A'),
    []
  );

  // SignalR real-time connection
  const connectionRef = useRef<HubConnection | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const isSignalREnabled = process.env.REACT_APP_ENABLE_SIGNALR_CONNECTION === 'true';
    if (!token || !isSignalREnabled) return;
    if (startedRef.current) return;

    const authService = new AuthService();
    const hubUrl = `${
      process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? ''
    }/api/notification/signalr-hubs/bellNotification`;

    const connection = createSignalRConnection({
      url: hubUrl,
      axiosInstance,
      accessTokenFactory: async () => authService.getTokenForSignalR(),
    });

    connectionRef.current = connection;

    const start = async () => {
      try {
        await connection.start();
        startedRef.current = true;
      } catch {
        setTimeout(() => void start(), 5000);
      }
    };

    connection.onclose(() => {
      startedRef.current = false;
      void start();
    });

    connection.on('ReceiveBellNotification', (_user: string, message: any) => {
      void refetchCount();
      setLiveMessages((prev) => [message, ...prev]);
    });

    void start();

    return () => {
      startedRef.current = false;
      void connection.stop();
    };
  }, [token]);

  return {
    count,
    refetchCount: useCallback(() => void refetchCount(), [refetchCount]),
    notifications,
    notificationsLoading,
    deletingId,
    onDelete,
    onClearAll,
    onItemClick,
    formatDate,
  };
}
